import {
  AccountStruct,
  ChatResponseMsg,
  ChatType,
  GameErrIDs,
  sendMessage,
  UGLY_USER_STATE,
} from "./api";
import { ChallengeContextInterface } from "./App";
import { EventBus } from "./game/EventBus";
import { toast } from "./components/Toasts";
import { EVENTS_NAME } from "./game/consts.ts";

// Interface for the websocket handlers
export interface WebSocketHandlers {
  setAccount?: (account: AccountStruct | null) => void;
  setChallengeContext?: (
    challengeContext:
      | ChallengeContextInterface
      | ((prevState: ChallengeContextInterface) => ChallengeContextInterface),
  ) => void;
  addChatMessage?: (message: string) => void;
  // Called when the dispatcher rejects our PlayerJoin (stale/consumed magic).
  // Should log the user out (clear cached account + magic) and reset join
  // state so a fresh login can join cleanly.
  onJoinRejected?: () => void;
}

// The most recently created socket — see onclose stale-socket guard.
let activeWs: WebSocket | null = null;

// Function to create and initialize the websocket
export const createWebSocket = (
  handlers: WebSocketHandlers,
  onWebSocketSet: (ws: WebSocket) => void,
): WebSocket | null => {
  // Determine the WebSocket URL based on environment
  const wsBase = window.location.href.includes("localhost")
    ? "ws://localhost:1244/ws"
    : window.location.href.includes("dev")
      ? "wss://dev.reali7y-over.run/ws"
      : "wss://reali7y-over.run/ws";
  // const wsBase = "ws://localhost:1244/ws";

  console.log("======>Creating ws from " + wsBase);
  const ws = new WebSocket(wsBase);
  // Track the newest socket: if a stale (already replaced) socket closes, it
  // must not clear the account or spawn a second reconnect loop.
  activeWs = ws;

  // Handle websocket open event
  ws.onopen = function (_event) {
    console.log("======>on open");
    // Signal the app so it can PlayerJoin as soon as the socket is live —
    // no need to wait for the Phaser game to boot. Early join = we get the
    // player's PlayerInfo (incl. their saved avatar) on the account screen.
    EventBus.emit(EVENTS_NAME.wsOpen);
  };

  // Handle websocket close event
  ws.onclose = (_event) => {
    if (ws !== activeWs) {
      console.log("======>stale websocket closed (already replaced); ignoring");
      return;
    }
    console.log("======>WEBSOCKET CLOSED");
    // Reset the whole session, not just the account: joinedGame and the
    // single-flight join guard must also clear, otherwise after a server
    // restart the user can log back in but the join effect never re-fires
    // (joinedGame stuck true) — leaving them walking around silently
    // un-joined (server drops every PlayerMove; no fuel burn, no saves).
    if (handlers.onJoinRejected) {
      handlers.onJoinRejected();
    } else if (handlers.setAccount) {
      handlers.setAccount(null);
    }
    setTimeout(() => {
      const newWs = createWebSocket(handlers, onWebSocketSet);
      if (newWs) {
        onWebSocketSet(newWs);
      }
    }, 5000);
  };

  // Handle incoming messages
  ws.onmessage = function (event) {
    handleWebSocketMessage(event, handlers);
  };

  return ws;
};

// Function to handle websocket messages
const handleWebSocketMessage = (
  event: MessageEvent,
  handlers: WebSocketHandlers,
) => {
  const msg = JSON.parse(event.data);

  // Handle error messages
  if (msg.ErrResponse) {
    handleErrorResponseMessage(msg, handlers);
  } else {
    // Handle success messages
    handleSuccessMessage(msg, handlers);
  }
};

// Rate-limit for the PlayerNotFound alert (several in-flight interactions can
// all get this reply; one blocking alert is enough).
let lastPlayerNotFoundAlertMs = 0;

// Function to handle error response messages
const handleErrorResponseMessage = (
  msg: any,
  handlers: WebSocketHandlers = {},
) => {
  console.error("======>Hit error response condition; logging error!");
  console.error(msg);
  console.error(handlers);

  const errResponse = msg.ErrResponse;
  if (!errResponse) return;

  switch (errResponse.err_id) {
    case GameErrIDs.UnknownMessage:
      switch (true) {
        case /^Failed to accept mission/.test(errResponse.message):
          console.log("Failed to accept mission (probably already accepted)");
          break;
        case /^Player with magic .* not found/.test(errResponse.message):
          // Our cached magic is stale (the dispatcher has no pending join for
          // it — e.g. servers restarted, or the magic was already consumed).
          // Don't limp along in a half-joined state: log the user out, wipe
          // the cached account/magic, and let the Game view's account guard
          // bounce them to /account to log in fresh.
          console.error(
            "Join rejected (stale magic) — clearing cached account and returning to login",
          );
          if (handlers.onJoinRejected) {
            handlers.onJoinRejected();
          } else if (handlers.setAccount) {
            handlers.setAccount(null);
          }
          break;
        default:
          toast("Error", errResponse.message, "error");
      }
      break;
    case GameErrIDs.MessageForwardFailed:
      toast(
        "Server Error",
        "Message forwarding failed — this is a server error. Please notify the authorities.",
        "error",
      );
      break;
    case GameErrIDs.WrongPhrase:
      // EventBus.emit(SHOW_RESOURCE_INTERACTION_MODAL, {
      //   title: "Wrong Phrase",
      //   message: errResponse.message,
      // });
      EventBus.emit(EVENTS_NAME.wrongPhrase);
      break;
    case GameErrIDs.UnknownMob:
    case GameErrIDs.UnKnownNPC:
    case GameErrIDs.MobInteractionTooSoon:
      console.error(`Unhandled error: ${errResponse.err_id}`);
      break;

    case GameErrIDs.PlayerNotFound:
      // The game server has no player for this connection (stale join or a
      // dropped dispatcher<->game link) — interactions were silently no-oping.
      // Surface it, but at most once per window: several interactions can be
      // in flight at once and each gets this reply.
      if (Date.now() - lastPlayerNotFoundAlertMs > 15000) {
        lastPlayerNotFoundAlertMs = Date.now();
        toast("Connection Problem", errResponse.message, "error");
      } else {
        console.error(`PlayerNotFound (toast suppressed): ${errResponse.message}`);
      }
      break;

    default:
      console.error(`Unhandled error: ${errResponse.err_id}`);
      toast("Error", errResponse.message, "error");
  }
};

// Function to handle success messages
const handleSuccessMessage = (msg: any, handlers: WebSocketHandlers) => {
  // console.log(msg);
  // Handle LoadPlayerResult message
  if (msg.LoadPlayerResult) {
    // console.log(`LoadPlayerResult: setting player`);
    if (handlers.setAccount) {
      handlers.setAccount({
        account_name: msg.LoadPlayerResult.plr.account_name,
        password: "youwish",
        alcohol: parseInt(msg.LoadPlayerResult.plr.alcohol),
        health: parseInt(msg.LoadPlayerResult.plr.health),
        score: parseInt(msg.LoadPlayerResult.plr.score),
      });
    }
  }

  // Handle InteractNPCResponse message
  if (msg.InteractNPCResponse) {
    /* {
    "InteractNPCResponse": {
        "npc_id": 10031,
        "mission_list": [
            {
                "id": 160,
                "name": "6. Neon Kanji maze",
                "description": "Complete this interactive challenge and then use the final key as your answer!",
                "objectives": [
                    {
                        "id": 1,
                        "name": "Objective 1",
                        "description": "Complete the first objective",
                        "min_level_requirement": 1,
                        "min_alcohol": 1,
                        "missions_that_must_be_completed": [],
                        "required_items": []
                    }
                ],
                "rewards": [
                    6
                ]
            }
        ]
      }
    }*/
    // The InteractNPCResponse is now the authoritative source for a
    // challenge's data (fresh OR already-accepted) — no localStorage cache.
    // It makes the challenge AVAILABLE (lights up the "?" button) AND opens
    // the plain widget/popover on the FIRST entry into the zone (2026-08-02:
    // restored per Eddie — the earlier "walking in used to force the panel
    // open every time" complaint was about constant re-opening while
    // lingering, which PhaserGame.tsx's zone handler already guards against
    // (interactNpc/this response only fires once per zone-entry episode, not
    // on every ~1s re-check while standing still). This does NOT auto-launch
    // the full-screen rich interface — that still requires an explicit
    // "OPEN INTERFACE" click (richOpenedFor in ChallengeDialog.tsx), so a
    // rich challenge's popover shows the launch button, not the modal.
    const mission0 = msg.InteractNPCResponse.mission_list[0];
    const obj0 = (mission0.objectives && mission0.objectives[0]) || {};
    const chalCon = {
      challengeTitle: mission0.name,
      challengeText: mission0.description,
      challengeId: mission0.id,
      objectiveId: obj0.id ?? 1,
      // Rich-challenge rendering hints, authored server-side per objective.
      challengeType: obj0.challenge_type || "",
      challengePayload: obj0.challenge_payload || "",
      challengeAvailable: true,
      challengeDialogOpen: true,
      interactive: true,
      // Multi-objective missions send only the current (first-incomplete)
      // objective — this is the server's own verdict on whether ALL of them
      // are done, since the client can no longer infer that from seeing a
      // single objective's bitmask bit.
      allObjectivesComplete: !!mission0.all_objectives_complete,
      totalObjectives: mission0.total_objectives ?? 1,
    };
    if (handlers.setChallengeContext) {
      handlers.setChallengeContext(challengeContext => ({
        ...challengeContext,
        ...chalCon,
      }));
    }
    // console.log(chalCon);
  }

  // Handle ChatResponseMsg message
  if (msg.ChatResponseMsg) {
    const chatResponse: ChatResponseMsg = msg.ChatResponseMsg;
    let formattedMessage = "";

    // Format the message based on chat type
    switch (chatResponse.chat_type) {
      case ChatType.Say:
        formattedMessage = `[say] ${chatResponse.chat_from}: ${chatResponse.chat_string}`;
        break;
      case ChatType.Shout:
        formattedMessage = `[shout] ${chatResponse.chat_from}: ${chatResponse.chat_string}`;
        break;
      case ChatType.Broadcast:
        formattedMessage = `[broadcast] ${chatResponse.chat_from}: ${chatResponse.chat_string}`;
        break;
      case ChatType.DM:
        // Incoming DM (to current user)
        formattedMessage = `[dm from ${chatResponse.chat_from}] ${chatResponse.chat_string}`;
        break;
      default:
        formattedMessage = `${chatResponse.chat_from}: ${chatResponse.chat_string}`;
    }

    // Add the formatted message to the chat using either the handler passed to createWebSocket
    // or the globally registered handler
    if (handlers.addChatMessage) {
      handlers.addChatMessage(formattedMessage);
    }

    const globalHandler = getChatMessageHandler();
    if (globalHandler) {
      globalHandler(formattedMessage);
    }
  }

  // Handle PlayerScoreBoard message
  if (msg.PlayerScoreBoard) {
    // console.log("Got player scoreboard");
    localStorage.setItem(
      "playerScoreboard",
      JSON.stringify(msg.PlayerScoreBoard.player_score_board),
    );
  }

  // Handle PlayerInfo message
  if (msg.PlayerInfo) {
    // this is info from the local player
    // console.log("PlayerInfo: set account");
    // the eternal magic hash in this message is incorrect, so we don't use it
    //
    // player_file guard: a message is only "mine" if the name matches AND it
    // carries a real player_file. Cross-player broadcasts go through
    // GamePlayer::public_view() which BLANKS player_file, so even if another
    // client is logged into the SAME account (identical name), their broadcast
    // can't be mistaken for our own and clobber our account (that's the
    // "other player won -> I can no longer win" bug: their completed mission
    // 999 / won_at merged into our account). Self-authoritative sends (move
    // echo, mission complete, join) always carry the real guid.
    if (
      msg.PlayerInfo.player.account_name &&
      msg.PlayerInfo.player.player_file &&
      UGLY_USER_STATE.length === 1 &&
      msg.PlayerInfo.player.account_name === UGLY_USER_STATE[0]
    ) {
      // console.log("PlayerInfo: setting account from local player");
      if (handlers.setAccount) {
        handlers.setAccount({ ...msg.PlayerInfo.player });
      }
      EventBus.emit(EVENTS_NAME.playerInfo, msg.PlayerInfo.player);
    }
    // or this is info from remote players
    else {
      console.log("PlayerInfo: setting account from remote player");
      EventBus.emit(EVENTS_NAME.remotePlayerInfo, msg.PlayerInfo.player);
    }
  }

  // Handle EntitySnapshot — one batched message carrying everything in range
  // this tick, replacing the per-entity PlayerInfo/SupportMobInfo fan-out.
  if (msg.EntitySnapshot) {
    EventBus.emit(EVENTS_NAME.entitySnapshot, msg.EntitySnapshot);
  }

  // Handle GuessAIResult — the verdict on a /guessai attempt. Both outcomes
  // used to be silent (correct sent nothing; wrong rode an ErrResponse the
  // client only console.errored), leaving the laptop stuck on "Attempting to
  // guess AI: X". Toast it too: the player may have closed the laptop tab,
  // and a half-your-score penalty must never land unannounced.
  if (msg.GuessAIResult) {
    const r = msg.GuessAIResult;
    EventBus.emit(EVENTS_NAME.guessAiResult, r);
    toast(
      r.correct ? "AI Identified" : "Wrong Guess",
      r.message,
      r.correct ? "info" : "error",
    );
  }

  // Handle PlayerDied message — the explicit WASTED signal.
  // Death cannot be inferred from health: the server respawns the player to
  // FULL health the instant they hit 0, so the PlayerInfo that follows is
  // indistinguishable from a heal (the "bar hit 0, popped back up, I never
  // died" bug). This message is the only reliable trigger.
  if (msg.PlayerDied) {
    // Write the respawn straight into the cached account (localStorage too).
    // The WASTED screen restarts GameMain, and `create()` rebuilds player1
    // from `account.position` — if that cache still held the spot they died
    // on, the player respawned THERE and then reported it back to the server,
    // undoing the teleport. A partial object is safe: the merge in
    // localStorageWrappedSetAccount treats a missing account_name as
    // same-account and spreads over the previous state.
    if (handlers.setAccount) {
      handlers.setAccount({
        position: msg.PlayerDied.respawn_position,
        health: msg.PlayerDied.health,
        alcohol: msg.PlayerDied.alcohol,
      } as any);
    }
    EventBus.emit(EVENTS_NAME.playerDied, msg.PlayerDied);
  }

  // Con-wide world state (currently just "is the boss dead") — sent once
  // right after PlayerJoin (so a fresh login always learns current state)
  // and broadcast to everyone the instant the boss actually dies. Drives the
  // persistent Final Answers widget, which has no zone/NPC of its own.
  if (msg.WorldState) {
    EventBus.emit(EVENTS_NAME.worldState, msg.WorldState);
  }

  // Server-initiated "you left this zone" (2026-08-09). The client's map no
  // longer carries InteractiveChallenge/Storyline object positions (see
  // strip_client_map.py), so it can no longer detect walking away from one
  // itself — the server does that now and pushes this instead. Reuses the
  // exact same clearGameUi("challenge"/"story") handling PhaserGame.tsx
  // already has for the old client-local exit detection.
  if (msg.ClearGameUi) {
    EventBus.emit(EVENTS_NAME.clearGameUi, msg.ClearGameUi.kind);
  }

  // Handle MissionComplete message
  if (msg.MissionComplete) {
    // Prefer the authored challenge_response (a clue) as the custom success
    // output; fall back to the generic completion line if none was authored.
    const base: string =
      msg.MissionComplete.challenge_response &&
      msg.MissionComplete.challenge_response.trim().length > 0
        ? msg.MissionComplete.challenge_response
        : `Mission ${msg.MissionComplete.mission_id} completed! You received ${msg.MissionComplete.rewards_given.length} rewards.`;

    // Multi-objective missions serve ONE objective at a time, so a correct
    // answer silently advances you to the next puzzle. Without saying so, a
    // player who resubmits in the same zone has their (correct!) answer
    // rejected -- the server is already asking for the next objective -- and
    // it reads as "my right answer did nothing". Say what just happened.
    const done = msg.MissionComplete.objectives_done ?? 0;
    const total = msg.MissionComplete.objectives_total ?? 0;
    let progress = "";
    if (total > 1) {
      progress =
        done >= total
          ? `\n\n[ ${done} / ${total} OBJECTIVES COMPLETE — SECTOR CLEARED ]`
          : `\n\n[ ${done} / ${total} OBJECTIVES COMPLETE ]\nLeave this zone and step back in for the next one.`;
    }
    EventBus.emit(EVENTS_NAME.correctPhrase, base + progress);
    // 0a's cipher — the multibase-number haiku, the hardest authored puzzle
    // in the game and the only one signed by a real person. It gets a party.
    // Gated on the local player: MissionComplete is only ever sent to the
    // player who completed it, but the account check costs nothing and keeps
    // this from ever firing off someone else's broadcast.
    const CIPHER_MISSION_ID = 5000;
    if (
      msg.MissionComplete.mission_id === CIPHER_MISSION_ID &&
      msg.MissionComplete.player?.account_name === UGLY_USER_STATE[0]
    ) {
      EventBus.emit(EVENTS_NAME.cipherVictory, {
        mission_id: CIPHER_MISSION_ID,
        response: base,
      });
    }
    // Sync the fresh player (with the just-appended field-note) into the account
    // so the Notes tab shows the new trace immediately, not on the next move.
    if (
      handlers.setAccount &&
      msg.MissionComplete.player &&
      msg.MissionComplete.player.account_name === UGLY_USER_STATE[0]
    ) {
      handlers.setAccount({ ...msg.MissionComplete.player });
    }
  }

  // A player disconnected (or despawned): tell the game to remove their
  // sprite, otherwise it lingers as a ghost at their last position forever.
  if (msg.RemovePlayer) {
    EventBus.emit(EVENTS_NAME.removePlayer, msg.RemovePlayer.account_name);
  }

  // ── Server-authoritative combat monsters ──
  if (msg.SupportMobInfo) {
    EventBus.emit(EVENTS_NAME.supportMobInfo, msg.SupportMobInfo.mob_info);
  }
  if (msg.RemoveSupportMob) {
    EventBus.emit(EVENTS_NAME.removeSupportMob, msg.RemoveSupportMob.server_iid);
  }
  if (msg.MobControl) {
    // Controller lease: this client is (no longer) simulating that mob.
    EventBus.emit(EVENTS_NAME.mobControl, msg.MobControl);
  }

  // Server-backed storyline: forward the fetched text to the game, which shows
  // the story widget only if the player is still standing in that story rect.
  if (msg.StorylineResponse) {
    EventBus.emit(EVENTS_NAME.storylineResponse, {
      story_id: msg.StorylineResponse.story_id,
      story_title: msg.StorylineResponse.story_title,
      story: msg.StorylineResponse.story,
    });
  }

  // Handle InteractMobResponse message
  if (msg.InteractMobResponse) {
    // console.log("Got mob interaction response");
    if (msg.InteractMobResponse.err_id) {
      toast(
        "Mob Interaction Failed",
        msg.InteractMobResponse.error_message || "Error interacting with mob",
        "error",
      );
    } else {
      if (msg.InteractMobResponse.item_list.length > 0) {
        toast(
          "Items Received",
          `You received ${msg.InteractMobResponse.item_list.length} items from the mob.`,
        );
      }
    }
  }

  // Handle InteractResourceResponse message
  if (msg.InteractResourceResponse) {
    // console.log(
    //   `Resource interaction with resource ID: ${msg.InteractResourceResponse.resource_id}`,
    // );
    if (msg.InteractResourceResponse.err_id) {
      // Show error message in modal
      toast(
        "Resource Interaction Failed",
        msg.InteractResourceResponse.error_message ||
          "Error interacting with resource",
        "error",
      );
      // console.log(
      //   `Error interacting with resource: ${msg.InteractResourceResponse.error_message}`,
      // );
    } else {
      if (msg.InteractResourceResponse.item_list.length > 0) {
        // item_list entries are SDItems: { id, name, value, ... } where the
        // granted amount is `value`. Special ids (see GamePlayer::
        // update_from_received_items): 1 = alcohol/fuel, 2 = health.
        const itemList: { id: number; name: string; value: number }[] =
          msg.InteractResourceResponse.item_list;
        let messageText = "";

        const totalFuel = itemList
          .filter((item) => item.id === 1)
          .reduce((sum, item) => sum + item.value, 0);
        const totalHealth = itemList
          .filter((item) => item.id === 2)
          .reduce((sum, item) => sum + item.value, 0);

        if (totalFuel > 0) {
          messageText += `⛽ +${totalFuel} fuel. `;
        }
        if (totalHealth > 0) {
          messageText += `❤️ +${totalHealth} health. `;
        }

        // Anything else (quest items, points), report generically by name
        const otherItems = itemList.filter(
          (item) => item.id !== 1 && item.id !== 2,
        );
        if (otherItems.length > 0) {
          messageText += otherItems
            .map((item) => `Received: ${item.name}.`)
            .join(" ");
        }

        // Show success message in modal
        toast("Resource Collected", messageText.trim());

        // The response carries the updated player — refresh the banner
        // immediately instead of waiting for the next move's PlayerInfo.
        if (msg.InteractResourceResponse.player) {
          EventBus.emit(
            EVENTS_NAME.playerInfo,
            msg.InteractResourceResponse.player,
          );
        }

        // console.log(
        //   `Received ${msg.InteractResourceResponse.item_list.length} items from the resource`,
        // );
      } else {
        // No items received
        toast("Resource Empty", "You didn't receive any items from this resource.");
      }
    }
  }

  // Handle Pong message
  if (msg.Pong) {
    // console.log("Got pong response");
    console.log(`Pong received with timestamp: ${msg.Pong.timestamp}`);
  }
};

// Global chat message handler that can be registered by components
let globalChatMessageHandler: ((message: string) => void) | null = null;

// Function to register a chat message handler
export const registerChatMessageHandler = (
  handler: (message: string) => void,
) => {
  globalChatMessageHandler = handler;
  console.log("======>Chat message handler registered");
};

// Function to get the global chat message handler
export const getChatMessageHandler = () => {
  return globalChatMessageHandler;
};

// Function to start the ping-pong mechanism to keep the connection alive
export const startPingPong = (ws: WebSocket): NodeJS.Timeout => {
  return setInterval(() => {
    if (ws) {
      sendMessage(ws, { Ping: { timestamp: new Date().getTime() } });
    }
  }, 1000 * 60);
};
